import { BubbleConfig } from '@/components/public/Visualisations/Bubbles/types'
import {
  BubbleMetadata,
  ClusterAndHeaderBoxMetadata,
} from '@/components/public/Visualisations/BubblesV2/Metadata'
import { SORT_BY_CLUSTER_PATH_OPTION_VALUE, SORT_BY_CLUSTER_SIZE_OPTION_VALUE } from '@/constants'
import { measureText } from '@/helpers/canvasHelpers'
import logger from '@/plugins/logging/logger'
import {
  ComparisonEqualityOperators,
  Coordinate2D,
  CustomBubblesCompareCategory,
  CustomBubblesFilter,
  HaloBubblesConfig,
  Rectangle,
  SortDirection,
  VisNode,
} from '@/types'

export type ClustersHash = number

export interface ClusterAndHeaderPositions {
  cluster: Coordinate2D
  header: Coordinate2D
}
export interface BubblesLayout {
  zoomFactor: number
  mainClustersPositionsByPath: Record<string, ClusterAndHeaderPositions>
  offset: Coordinate2D
  allEntitiesBoundingBox: Rectangle
  mainClustersBoundingBox: Rectangle
  sideBubblesClusterBoundingBox: Rectangle
}

export function shouldBubblesAmalgamate(bubbles: BubbleMetadata[], canvasSize: number) {
  // If a bubble is within 10% of its destination then it is considered near its destination
  const nearDestinationThreshold = 10
  let count = 0

  for (const bubble of bubbles) {
    const distanceX = bubble.destinationPosition.x - bubble.currentPosition.x
    const distanceY = bubble.destinationPosition.y - bubble.currentPosition.y
    const distanceTotal = Math.sqrt(distanceX ** 2 + distanceY ** 2)
    const diagonalDistance = Math.sqrt(canvasSize ** 2 + canvasSize ** 2)
    const distancePercentage = diagonalDistance === 0 ? 0 : (distanceTotal / diagonalDistance) * 100

    if (distancePercentage <= nearDestinationThreshold) {
      count++
    }
  }

  // The bubbles should amalgamate if 20% or more of them are near their destination
  return (count / bubbles.length) * 100 >= 20
}

export function findActiveHaloConfig(
  config: BubbleConfig,
  visNode: VisNode,
  filter: (c: HaloBubblesConfig) => boolean,
) {
  return config.haloBubbles.filter(filter).find((haloBubblesConfig: HaloBubblesConfig) => {
    const filterKey = haloBubblesConfig.filter
    if (filterKey && filterKey in config.customBubblesGroups) {
      const { filters } = config.customBubblesGroups[filterKey]
      for (const filter of filters) {
        if (checkHaloBubblesFilter(filter, visNode)) {
          return true
        }
      }
    }
    return false
  })
}

function checkHaloBubblesFilter(filter: CustomBubblesFilter, visNode: VisNode) {
  const { category, compare, operator, value } = filter
  const categoryData = visNode.data[category]
  const compareTo = compare === CustomBubblesCompareCategory ? visNode.data[value] : value
  switch (operator) {
    case ComparisonEqualityOperators.LESS_THAN:
      return Number(categoryData) < Number(compareTo)
    case ComparisonEqualityOperators.GREATER_THAN:
      return Number(categoryData) > Number(compareTo)
    case ComparisonEqualityOperators.LESS_THAN_OR_EQUAL:
      return Number(categoryData) <= Number(compareTo)
    case ComparisonEqualityOperators.GREATER_THAN_OR_EQUAL:
      return Number(categoryData) >= Number(compareTo)
    case ComparisonEqualityOperators.STRICT_EQUAL:
      if (isNaN(Number(categoryData)) && isNaN(Number(compareTo))) return categoryData === compareTo
      else return Number(categoryData) === Number(compareTo)
    case ComparisonEqualityOperators.STRICT_UNEQUAL:
      if (isNaN(Number(categoryData)) && isNaN(Number(compareTo))) return categoryData !== compareTo
      else return Number(categoryData) !== Number(compareTo)
    default:
      return false
  }
}

// calculates the vertical offset required to center align
// the provided text based on the existence of a descender
// in the text
export function calculateDescenderOffset(
  text: string,
  fontSize: number,
  fontFamily: string,
): number {
  const metrics = measureText(text, fontSize, fontFamily)
  if (metrics.actualBoundingBoxDescent > 0) {
    return 0
  }

  return metrics.actualBoundingBoxDescent
}

export function sortClusters(
  boxes: ClusterAndHeaderBoxMetadata[],
  sortBy: string,
  direction: SortDirection,
): ClusterAndHeaderBoxMetadata[] {
  try {
    if (sortBy === SORT_BY_CLUSTER_SIZE_OPTION_VALUE) {
      return sortClustersBySize(boxes, direction)
    }
    if (sortBy === SORT_BY_CLUSTER_PATH_OPTION_VALUE) {
      return sortClustersByPath(boxes, direction)
    }

    return sortClustersByField(boxes, sortBy, direction)
  } catch (e) {
    logger.error('Error sorting clusters', e)
    return boxes
  }
}

function sortClustersBySize(
  boxes: ClusterAndHeaderBoxMetadata[],
  direction: SortDirection,
): ClusterAndHeaderBoxMetadata[] {
  const sorted = boxes.sort((b, a) => a.clusterWidth - b.clusterWidth)
  if (direction === SortDirection.ASCENDING) {
    return sorted.reverse()
  }
  return sorted
}

function sortClustersByPath(boxes: ClusterAndHeaderBoxMetadata[], direction: SortDirection) {
  const sorted = boxes.sort((a, b) =>
    a.path.toString().localeCompare(b.path.toString(), undefined, { sensitivity: 'base' }),
  )

  if (direction === SortDirection.ASCENDING) {
    return sorted.reverse()
  }
  return sorted
}

function sortClustersByField(
  boxes: ClusterAndHeaderBoxMetadata[],
  sortBy: string,
  direction: SortDirection,
): ClusterAndHeaderBoxMetadata[] {
  const sorted = boxes.sort(
    (b, a) => a.getAggregatedFieldValue(sortBy) - b.getAggregatedFieldValue(sortBy),
  )
  if (direction === SortDirection.ASCENDING) {
    return sorted.reverse()
  }
  return sorted
}
